import { redirect } from 'react-router-dom';

import { getUserByOktaUserId } from '@app/actions/auth';
import { getFullOrganizationData } from '@app/actions/organization';
import { getAutoReplies, getUserProperties } from '@app/actions/profile';
import { getOktaIdToken, getOktaUsername } from '@app/helpers/localStorage';
import { isLoadedFromHiMarleyConnect } from '@app/helpers/platform';
import { prefetchCasesQuery } from '@app/services/case/cases';
import { prefetchGroupsQuery } from '@app/services/group';
import { prefetchOperatorsQuery } from '@app/services/operator';
import { prefetchEffectiveFeatures } from '@app/services/organization-features';
import { getKeys } from '@app/slices/templates';
import store from '@app/store';
import { StateType } from '@app/types/reducer-state';
import {
  getBootstrapDataFetched,
  setBootstrapDataFetched,
  getInboxContextDataFetched,
  setInboxContextDataFetched,
  getLastOktaUserId,
  setLastOktaUserId,
} from '@app/utils/auth-loader-prefetch';

const STALE_TIME = 3000;

type InboxFilters = StateType['profile']['properties']['inboxFilters'];

const hasInboxFilters = (inboxFilters?: InboxFilters) =>
  !!(
    inboxFilters &&
    typeof inboxFilters === 'object' &&
    Object.keys(inboxFilters).length > 0
  );

const buildInboxCustomFilters = (params: {
  inboxFilters: InboxFilters;
  expandInboxSearchBar?: boolean;
}) => {
  const { inboxFilters, expandInboxSearchBar } = params;
  return {
    inboxFilters,
    sort: inboxFilters?.sort,
    order: inboxFilters?.order,
    secondarySort: inboxFilters?.secondarySort,
    secondaryOrder: inboxFilters?.secondaryOrder,
    expandInboxSearchBar,
  } as const;
};

const shouldEnableGroups = (inboxFilters?: InboxFilters) => {
  if (!inboxFilters) return false;
  return Object.keys(inboxFilters?.groups || {}).some(
    (key) => inboxFilters?.groups?.[key] === null,
  );
};

const prefetchBootstrapData = ({ oktaUserId }: { oktaUserId: string }) => {
  // Redux bootstrapping actions
  store.dispatch(getKeys());
  store.dispatch(getFullOrganizationData());
  store.dispatch(getUserByOktaUserId(oktaUserId));
  store.dispatch(getUserProperties());

  prefetchGroupsQuery({ searchText: '' }, { staleTime: STALE_TIME });
  prefetchEffectiveFeatures();
};

const prefetchInboxContextData = ({
  authId,
  inboxFilters,
  expandInboxSearchBar,
}: {
  authId: string;
  inboxFilters: InboxFilters;
  expandInboxSearchBar: boolean;
}) => {
  // USER_LIST
  prefetchOperatorsQuery({}, { staleTime: STALE_TIME });
  prefetchOperatorsQuery({ ids: [authId] }, { staleTime: STALE_TIME });
  store.dispatch(getAutoReplies(authId));
  // JOB LIST - CASES
  const customFilters = buildInboxCustomFilters({
    inboxFilters: inboxFilters as InboxFilters,
    expandInboxSearchBar,
  });
  prefetchCasesQuery(customFilters, authId, { staleTime: STALE_TIME });
  // GROUPS
  const ids = Object.keys(inboxFilters?.groups || {});
  prefetchGroupsQuery(
    { ids },
    { enabled: shouldEnableGroups(inboxFilters), staleTime: STALE_TIME },
  );
};

const requiredAuthLoader = async () => {
  let oktaUserId = await getOktaUsername();
  if (!oktaUserId && !isLoadedFromHiMarleyConnect()) {
    console.log('No username found, skipping bootstrap data');
    return redirect('/login');
  }

  if (!oktaUserId) {
    oktaUserId = getOktaIdToken()?.claims?.preferred_username;
  }

  const state = store.getState() as unknown as StateType;
  const authId = state?.auth?.user?._id;
  const inboxFilters = state?.profile?.properties?.inboxFilters;
  const expandInboxSearchBar = state?.jobs?.expandInboxSearchBar;

  const currentOktaId = oktaUserId || null;

  // these conditionals are mainly due to redux calls triggering the loader multiple times
  // we can remove these conditionals once we fully migrate to react-query

  // Reset flags if user changed
  if (getLastOktaUserId() !== currentOktaId) {
    setBootstrapDataFetched(false);
    setInboxContextDataFetched(false);
    setLastOktaUserId(currentOktaId);
  }

  // Bootstrap data once per auth session
  if (!getBootstrapDataFetched()) {
    prefetchBootstrapData({ oktaUserId });
    setBootstrapDataFetched(true);
  }

  // Inbox-context data once per (authId, inboxFilters)
  if (
    authId &&
    hasInboxFilters(inboxFilters) &&
    !getInboxContextDataFetched()
  ) {
    prefetchInboxContextData({
      authId,
      inboxFilters,
      expandInboxSearchBar,
    });
    setInboxContextDataFetched(true);
  }

  return null;
};

export default requiredAuthLoader;
